import { CustomerService, IApplicationFilter, PortfolioReportCache, property, widget } from '@dynamik/common';
import { BankTotal, InstrumentValueGroup } from '@dynamik/common/clients/reporting-clients';
import { BindingEngine } from 'aurelia-binding';
import { WidgetBase } from '@dynamik/common';
import { customElement } from 'aurelia-templating';

declare module '@dynamik/common/clients/reporting-clients' {
  interface InstrumentValueGroup {
    costPricePercent?: number;
    profitLossYearToDatePercent?: number;
    estimatedValueChange?: number;
  }

  interface IInstrumentValueGroup {
    costPricePercent?: number;
    profitLossYearToDatePercent?: number;
    estimatedValueChange?: number;
  }
}

interface IPortfolioOverviewByTypeLightWidgetData {
  instrumentValueGroups: InstrumentValueGroup[];
  bankTotal: BankTotal;
}

@customElement('portfolio-overview-by-type-light-widget')
@widget('Portfolio overview by type (light)', true, false, 'Description')
export class PortfolioOverviewByTypeLightWidget extends WidgetBase {
  constructor(element: Element, customerService: CustomerService, private portfolioReportCache: PortfolioReportCache, bindingEngine: BindingEngine) {
    super(element, bindingEngine, customerService);
  }

  data: InstrumentValueGroup[];
  bankTotal: BankTotal;

  @property('Data')
  precision: number = 2;

  @property('Table')
  font?: string;

  @property('Table')
  fontSize?: number = 10;

  @property('Table')
  color?: string;

  @property('Table')
  rowHeight?: number;

  @property()
  captionFont?: string;

  @property()
  captionFontSize?: number = 10;

  @property()
  captionColor?: string;

  protected async loadData(filter: IApplicationFilter): Promise<IPortfolioOverviewByTypeLightWidgetData> {
    const data = await Promise.all([
      this.portfolioReportCache.instrumentValueGroupsOverTime(filter.customer.companyId, undefined, filter.dates.fromStr, filter.dates.toStr, false),
      this.portfolioReportCache.bankTotalsOverTime(filter.customer.companyId, filter.dates.fromStr, filter.dates.toStr, false)
    ]);
    // we're only interested in the last date
    return {
      instrumentValueGroups: data[0].filter(x => x.date.substring(0, 10) == filter.dates.toStr),
      bankTotal: data[1].find(x => x.date.substring(0, 10) == filter.dates.toStr)
    };
  }

  protected async setData(data: IPortfolioOverviewByTypeLightWidgetData) {
    this.data = data.instrumentValueGroups;
    if (data.bankTotal) {
      this.data.push(new InstrumentValueGroup({
        group: 'Bank',
        estimatedValue: data.bankTotal.amount,
        estimatedValueFrom: data.bankTotal.amountFrom,
        costPrice: 0,
        costPriceYearToDateAdjusted: 0,
        profitLossYearToDate: 0
      }));
    }
    const total = this.getTotal(this.data);
    this.data.forEach(x => {
      x.costPrice = Math.abs(x.costPrice);
      x.costPricePercent = x.costPrice / total.costPrice * 100;
      x.profitLossYearToDatePercent = x.costPriceYearToDateAdjusted !== 0 ? x.profitLossYearToDate / x.costPriceYearToDateAdjusted * 100 : 0;
      x.estimatedValueChange = x.estimatedValue - x.estimatedValueFrom;
    });
     this.data.sort((a, b) => b.profitLossYearToDatePercent - a.profitLossYearToDatePercent);
     this.data.push(total);
     this.bankTotal = data.bankTotal;

     // Populate excelData for right-click export (exclude totals)
     const exportData = this.data.filter(x => x.group !== 'TOTAL');
     this.excelData = {
       fileName: 'PortfolioOverviewByTypeLight',
       data: {
         Items: exportData.map((x: any) => ({
           'Asset Type': x.group || 'Unknown',
           'Estimated Value From': x.estimatedValueFrom,
           'Estimated Value': x.estimatedValue,
           'Value Change': x.estimatedValueChange,
           'Cost Price': x.costPrice,
           'Cost Price %': x.costPricePercent,
           'Profit/Loss YTD': x.profitLossYearToDate,
           'Cost Price YTD Adjusted': x.costPriceYearToDateAdjusted,
           'Profit/Loss YTD %': x.profitLossYearToDatePercent
         }))
       }
     };
   }

  getTotal(x: InstrumentValueGroup[]) {
    const total = x.reduce((p, c) => {
      p.estimatedValueFrom += c.estimatedValueFrom;
      p.estimatedValue += c.estimatedValue;
      p.costPrice += Math.abs(c.costPrice);
      p.profitLossYearToDate += c.profitLossYearToDate;
      p.costPriceYearToDateAdjusted += c.costPriceYearToDateAdjusted;
      return p;
    }, new InstrumentValueGroup({
      estimatedValueFrom: 0, estimatedValue: 0, costPrice: 0, profitLossYearToDate: 0, costPriceYearToDateAdjusted: 0, costPricePercent: 100
    }));
    total.estimatedValueChange = total.estimatedValue - total.estimatedValueFrom;
    total.profitLossYearToDatePercent = total.costPriceYearToDateAdjusted !== 0 ? total.profitLossYearToDate / total.costPriceYearToDateAdjusted * 100 : 0;
    return total;
  }

  override configChanged(key: keyof PortfolioOverviewByTypeLightWidget) {
    switch (key) {
      case 'precision':
        this.refresh$.next();
        break;
      default:
        break;
    }
  }
}
